import { useOktaAuth } from '@okta/okta-react';
import { useEffect, useState } from 'react';
import { isOktaConfigured } from './okta-config';

// Identity helpers backed by the @okta/okta-react context. Two callers use
// them today: <Sidebar> renders the signed-in email, and the NR-identity
// hook in main.jsx feeds the user into the Browser agent so RUM events are
// attributed.
//
// `useCurrentUser` is only safe to call from components rendered INSIDE
// <Security> \u2014 i.e. inside SecureRoute. Components that render in both
// secured and anonymous trees must check `isOktaConfigured()` at the call
// site and fall back to `getAnonymousUser()` when false. This keeps the
// rules-of-hooks lint happy while still letting us render the same Layout
// in both modes.

const ANONYMOUS = Object.freeze({
  email: 'anonymous',
  name: 'Anonymous',
  subject: 'anonymous',
  authenticated: false,
});

function userFromIdToken(authState) {
  const claims = authState?.idToken?.claims;
  if (!claims) return null;
  return {
    email: claims.email || claims.preferred_username || 'unknown',
    name: claims.name || claims.email || 'Authenticated user',
    subject: claims.sub,
    preferredUsername: claims.preferred_username,
    authenticated: true,
  };
}

/**
 * Hook returning the current authenticated user. MUST be called inside
 * <Security>. Returns `{ user, loading, oktaAuth }`.
 */
export function useCurrentUser() {
  const { authState, oktaAuth } = useOktaAuth();
  const [user, setUser] = useState(() => userFromIdToken(authState) || ANONYMOUS);

  useEffect(() => {
    setUser(userFromIdToken(authState) || ANONYMOUS);
  }, [authState]);

  return {
    user,
    loading: !authState,
    oktaAuth,
  };
}

export function getAnonymousUser() {
  return ANONYMOUS;
}

export function isAuthEnabled() {
  return isOktaConfigured();
}
